import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DurchfuehrungsTag } from '@chancenwerk/_cms/products/entities/Product.js';
import type { Course } from '@chancenwerk/db/courses/Course.js';
import { CourseIntensive } from '@chancenwerk/db/courses/CourseIntensive.js';
import { CourseIntensiveSingle } from '@chancenwerk/db/courses/CourseIntensiveSingle.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @customElement chancen-component-choose-intensive-courses
 *
 * Shows boxes to choose the IntensiveCourses for a Student based on some possible courses.
 *
 * **Possible attrubutes:**
 *
 * - `intensiveCourses: Course[]` - The courses to choose from
 * - `student: Student` - The student to mutate
 * - `deactivateLock: boolean` - If the lock should be deactivated
 * - `maximumChoosen: number` - The maximum amount of courses that can be choosen
 * - `studentField: 'intensiveCoursesChoosen' | 'intensiveCoursesSingleChoosen'` - The field to mutate
 *
 * **possible events:**
 *
 * - `value-picked: ValueEvent<Student>` - Emitted when the Student was changed
 * - `intensiveCoursesChoosen-picked: ValueEvent<DurchfuehrungsTag[]>` - Emitted when the intensiveCoursesChoosen was changed
 * - `intensiveCoursesSingleChoosen-picked: ValueEvent<DurchfuehrungsTag[]>` - Emitted when the intensiveCoursesSingleChoosen was changed
 */
@customElement('chancen-component-choose-intensive-courses')
export class ChancenComponentChooseIntensiveCourses extends ChemistryLitElement {
  // ! Use of hasChanged comes from a problem in the registration formular.
  // ! This should be removed when the correct refactoring is done.
  @property({ attribute: false, hasChanged: () => true }) student?: Student;

  @property({ attribute: false }) intensiveCourses: Course[] = [];

  @property({ attribute: false }) deactivateLock = false;
  @property({ attribute: false }) maximumChoosen = 2;
  @property({ attribute: false }) studentField: 'intensiveCoursesChoosen' | 'intensiveCoursesSingleChoosen' =
    'intensiveCoursesChoosen';

  private _studentField() {
    if (!this.student) {
      return [];
    }
    return (this.student[this.studentField] ??= []);
  }

  private _emitStudentChanged() {
    const event = new CustomEvent('value-picked', {
      detail: {
        value: this.student,
      },
    });
    const choosenEvent = new CustomEvent(this.studentField + '-picked', {
      detail: {
        value: this._studentField(),
      },
    });
    this.dispatchEvent(event);
    this.dispatchEvent(choosenEvent);
  }

  private _findIndex(c: Course) {
    // * We can't identify CourseIntensiveSingle just by the time and weekday.
    // * So we need to check the cid as well.
    const needcid = c instanceof CourseIntensiveSingle;

    return this._studentField().findIndex(
      d =>
        d.wochentag === c.weekday &&
        d.startZeit?.toFormat('HH:mm') === c.startTime?.toFormat('HH:mm') &&
        (!needcid || d.cid === c._id),
    );
  }

  private _buildInfo(c: Course) {
    if (c instanceof CourseIntensiveSingle) {
      return `${c.startTime?.toFormat('dd.MM.yyyy')} ${c.startTime?.toFormat('HH:mm')} - ${c.endTime?.toFormat(
        'HH:mm',
      )} ${c.name}`;
    }

    if (c instanceof CourseIntensive) {
      return `${c.startTime?.toFormat('HH:mm')} - ${c.endTime?.toFormat('HH:mm')} ${c.weekday} ${[
        c.subjectOne,
        c.subjectTwo,
      ]
        .filter(Boolean)
        .join(', ')}`;
    }

    return '';
  }

  private _onChoiceClick(deactivated: boolean, c: Course) {
    if (deactivated) return;

    const index = this._findIndex(c);
    if (index === undefined || index === -1) {
      this._studentField().push(
        new DurchfuehrungsTag({
          wochentag: c.weekday,
          startZeit: c.startTime,
          endZeit: c.endTime,
          locations: [c.location],
          cid: c._id,
          info: this._buildInfo(c),
        }),
      );
    } else {
      this._studentField().splice(index, 1);
    }

    this._emitStudentChanged();
    this.requestUpdate();
  }

  private _checkDeactivated(choosen: boolean, c: Course): boolean {
    if (this.deactivateLock) return false;
    if (!choosen) {
      if (this._studentField() && this._studentField().length >= this.maximumChoosen) {
        return true;
      }

      if (
        this.student?.lernfoerderungDuerchfuehrungsTagChoosen?.some(
          d => d.startZeit?.toFormat('HH:mm') === c.startTime?.toFormat('HH:mm') && d.wochentag === c.weekday,
        )
      ) {
        return true;
      }
    }

    return false;
  }

  private _choiceItem(deactivated: boolean, choosen: boolean, c: Course) {
    return html`
      <cr-basic-card
        ${css({
          boxShadow: 'none',
          cursor: deactivated ? 'not-allowed' : 'pointer',
          background: deactivated
            ? this.colors.neutral.shade(300)
            : choosen
            ? this.colors.primary.shade(100)
            : this.colors.neutral.shade(100),
        })}
        @click=${() => this._onChoiceClick(deactivated, c)}
      >
        <cr-flex horizontal space="md" crossaxis-center>
          <cr-icon
            ${css({
              color: deactivated ? this.colors.neutral.shade(800) : choosen ? this.colors.primary : this.colors.neutral,
              fontSize: '1.8rem',
            })}
          >
            ${deactivated ? 'not_interested' : choosen ? 'done' : 'close'}
          </cr-icon>
          <cr-flex space="sm" ${css({ minWidth: '250px' })} horizontal crossaxis-center>
            <cr-text ${css({ minWidth: '100px' })}> ${c.weekday}</cr-text>
            <cr-text>
              <nobr
                >${c instanceof CourseIntensiveSingle ? c.startTime?.toFormat('dd.MM.yyyy') : nothing}
                ${c.startTime?.toFormat('HH:mm')} - ${c.endTime?.toFormat('HH:mm')}</nobr
              >
              ${(() => {
                if (c instanceof CourseIntensive && (c.subjectOne !== '' || c.subjectTwo !== '')) {
                  return html`<nobr> <cr-text> ${c.subjectOne} ${c.subjectTwo} </cr-text> </nobr>`;
                }

                if (c instanceof CourseIntensiveSingle) {
                  return html` <cr-text> ${c.subject}: ${c.name} </cr-text> `;
                }

                return nothing;
              })()}
            </cr-text>
          </cr-flex>
        </cr-flex>
      </cr-basic-card>
    `;
  }

  override render() {
    return html` <cr-flex space="md">
      ${(this.intensiveCourses ?? []).length === 0
        ? html`<cr-basic-card ${css({ boxShadow: 'none', background: this.colors.tone.error.shade(100) })}>
            <cr-text>Leider stehen aktuell keine Termine für deine Auswahl zur Verfügung</cr-text>
          </cr-basic-card>`
        : nothing}
      ${repeat(
        this.intensiveCourses ?? [],
        c => c._id,
        c => {
          const choosen = this._findIndex(c) !== -1;
          return this._choiceItem(this._checkDeactivated(choosen, c), choosen, c);
        },
      )}
    </cr-flex>`;
  }
}
